import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { FloatingPinyinKeyboardToggleButton } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard.toggle-button";
import { Portal } from "@-/com.npmjs.react-native-teleport";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  LearnDeckContext,
  useLearnCardSurface,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import {
  persistentKeyboardKeepFocused,
  useCollapsiblePersistentKeyboardDismissButtonVisible,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { setText as setPersistentKeyboardText } from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { AnswerInputRow } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.footer._components.answer-input-row";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { LearnFlashcardHorizontalGestureBlocker } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import {
  getPinyinKeyboardKeyHeight,
  getPinyinKeyboardKeyWidth,
  PinyinKeyboard,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { toToneNumbersPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { usePinyinPreviewChange } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.pinyin-preview";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import {
  useExpectedAnswerSet,
  useIsCorrectAnswer,
  useSubmitAnswer,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.submit";
import { useDrillPageAnswerMode } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.answer-mode";
import { useDrillAnswerInputAutoFocus } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.answer-input-auto-focus";
import { useKeyboardState } from "@-/com.npmjs.react-native-keyboard-controller";
import { tinykeys } from "@-/com.npmjs.tinykeys";
import { Platform, Pressable, Text, View } from "@-/com.npmjs.react-native";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import useLatest from "@-/com.npmjs.use-latest";
import { DrillFooterPinyinInputbox } from "#drill-footer-pinyin-inputbox";
import { useHasMissedPronunciationToneRef } from "#use-has-missed-pronunciation-tone-ref";

const TONE_SUBMIT_BUTTONS = [
  { digit: "1", label: "First tone", mark: "ˉ" },
  { digit: "2", label: "Second tone", mark: "ˊ" },
  { digit: "3", label: "Third tone", mark: "ˇ" },
  { digit: "4", label: "Fourth tone", mark: "ˋ" },
  { digit: "0", label: "Neutral tone", mark: "˙" },
] as const;

const OPTIONAL_TONE_DIGIT_SUFFIX = /[0-5]?$/;

/** How long a wrong tone press keeps the input outlined red before it returns to neutral. */
const INCORRECT_TONE_FLASH_MS = 600;

function shouldIgnoreDrillPronunciationKeyboardEvent(event: KeyboardEvent): boolean {
  if (event.isComposing) {
    return true;
  }
  const target = event.target instanceof HTMLElement ? event.target : null;
  const targetTagName = target?.tagName;
  return (
    targetTagName === "INPUT" || targetTagName === "TEXTAREA" || target?.isContentEditable === true
  );
}

export function DrillFooterPronunciationView() {
  const [containerWidth, setContainerWidth] = useState(0);
  const { diameter } = useToolbarGridMetrics();
  const { isActive, isCurrent, presentationKey = "pronunciation-input" } = useLearnCardSurface();
  const [sessionId] = useContextSelectors(
    LearnDeckContext,
    (c) => c.state.current.presentation.sessionId,
  );

  const [
    isAnswerModeOpen,
    _answerInputState,
    isShuangpinKeyboardToggleHidden,
    isShuangpinKeyboardVisible,
    onToggleShuangpinKeyboard,
  ] = useContextSelectors(
    DrillPageContext,
    (c) => c.isAnswerModeOpen,
    (c) => c.answerInputState,
    (c) => c.isShuangpinKeyboardToggleHidden,
    (c) => c.isShuangpinKeyboardVisible,
    (c) => c.onToggleShuangpinKeyboard,
  );
  // Display and canonical source belong to the same native revision. Prefer the source for
  // tone editing: neutral `er0` displays as `er` and cannot be recovered from display alone.
  const [pinyinPreviewDisplay] = useContextSelectors(DrillPageContext, (c) => {
    const { 0: displayValue, 3: sourceValue = displayValue } = c.keyboardText;
    return sourceValue;
  });
  const pinyinPreview = useMemo(
    () => (pinyinPreviewDisplay.length === 0 ? "" : toToneNumbersPinyin(pinyinPreviewDisplay)),
    [pinyinPreviewDisplay],
  );
  const [setAnswerInputState] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.setAnswerInputState,
  );
  const { pinyinKeyboardLayout } = useActorUser();
  const handleChangePinyinPreview = usePinyinPreviewChange();
  const { openAnswerMode } = useDrillPageAnswerMode();
  const submitAnswerMutation = useSubmitAnswer();
  const revealMutation = useRevealMutation();
  const isKeyboardVisible = useKeyboardState((s) => s.isVisible);
  // The gloss identifies one reading. Both keyboard paths must require that reading and tone,
  // rather than accepting another pronunciation of the same character.
  const expectedAnswerSet = useExpectedAnswerSet();
  const correctPinyinList = useMemo(() => [...expectedAnswerSet], [expectedAnswerSet]);
  const isCorrectAnswer = useIsCorrectAnswer();

  // A wrong tone press flashes the input red and keeps the card unrevealed so the learner can try
  // another tone. Once they miss, the eventual reveal presets Again rather than Easy.
  const hasMissedToneRef = useHasMissedPronunciationToneRef();
  const incorrectToneFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (incorrectToneFlashTimeoutRef.current !== null) {
        clearTimeout(incorrectToneFlashTimeoutRef.current);
      }
    },
    [],
  );
  const handleToneSubmit = (digit: string) => {
    const answer = pinyinPreview.trim().replace(OPTIONAL_TONE_DIGIT_SUFFIX, digit);
    if (incorrectToneFlashTimeoutRef.current !== null) {
      clearTimeout(incorrectToneFlashTimeoutRef.current);
      incorrectToneFlashTimeoutRef.current = null;
    }
    if (isCorrectAnswer(answer)) {
      setAnswerInputState((prev) => ({
        ...prev,
        answer,
        presetRating: hasMissedToneRef.current ? 1 : 4,
        validationState: "correct",
      }));
      revealMutation.mutate();
      return;
    }
    hasMissedToneRef.current = true;
    setAnswerInputState((prev) => ({ ...prev, answer, validationState: "incorrect" }));
    incorrectToneFlashTimeoutRef.current = setTimeout(() => {
      incorrectToneFlashTimeoutRef.current = null;
      setAnswerInputState((prev) =>
        prev.validationState === "incorrect" ? { ...prev, validationState: null } : prev,
      );
    }, INCORRECT_TONE_FLASH_MS);
  };

  const handleCheckFailed = useCallback(
    (value: string) => {
      setAnswerInputState((prev) => ({
        ...prev,
        answer: value,
        validationState: "incorrect",
      }));
    },
    [setAnswerInputState],
  );

  const handleCheckPassed = useCallback(
    (value: string) => {
      setAnswerInputState((prev) => ({
        ...prev,
        answer: value,
        presetRating: 4,
        validationState: "correct",
      }));
      revealMutation.mutate();
    },
    [revealMutation, setAnswerInputState],
  );

  // Put the blinking cursor back in this card's pinyin input when arriving from the previous card's
  // rating with the keyboard still up (it only re-binds focus, never re-summons the keyboard).
  useDrillAnswerInputAutoFocus();

  useEffect(() => {
    if (isKeyboardVisible && !isAnswerModeOpen) {
      openAnswerMode();
    }
  }, [isAnswerModeOpen, isKeyboardVisible, openAnswerMode]);

  const isFocused = useIsFocused();
  const isFocusedRef = useLatest(isFocused && isActive);
  // Native-keyboard Enter / empty-space-forgot handling lives in DrillFooterPinyinInputbox (mirrors
  // the meaning answer box). These refs back the web hardware-keyboard handler below.
  const inputTextRef = useLatest(pinyinPreview);
  const submitAnswerRef = useLatest(submitAnswerMutation);
  const handleForgotShortcut = useCallback(() => {
    setPersistentKeyboardText("");
    setAnswerInputState((prev) => ({
      ...prev,
      answer: "",
      input: "",
      presetRating: 1,
      validationState: null,
    }));
    submitAnswerMutation.mutate();
  }, [setAnswerInputState, submitAnswerMutation]);
  const handleForgotShortcutRef = useLatest(handleForgotShortcut);

  const isAnswerModeOpenRef = useLatest(isAnswerModeOpen);
  const openAnswerModeRef = useLatest(openAnswerMode);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    return tinykeys(
      globalThis.window,
      {
        "[Shift]+([\\s\\S])": (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          if (!isAnswerModeOpenRef.current) {
            openAnswerModeRef.current();
          }
          const key = event.key;
          if (key === " " && inputTextRef.current.length === 0) {
            event.preventDefault();
            handleForgotShortcutRef.current();
            return;
          }
          // Physical keys are always typed literally, even while the shuangpin grid is showing: a
          // learner who wants shuangpin from a hardware keyboard switches their OS input method.
          event.preventDefault();
          handleChangePinyinPreview(inputTextRef.current + key);
        },
        "[Shift]+Backspace": (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          if (!isAnswerModeOpenRef.current) {
            openAnswerModeRef.current();
          }
          event.preventDefault();
          if (inputTextRef.current.length > 0) {
            handleChangePinyinPreview(inputTextRef.current.slice(0, -1));
          }
        },
        "[Shift]+Enter": (event) => {
          if (!isFocusedRef.current || !isAnswerModeOpenRef.current) {
            return;
          }
          event.preventDefault();
          submitAnswerRef.current.mutate();
        },
      },
      { ignore: shouldIgnoreDrillPronunciationKeyboardEvent },
    );
  }, [
    handleChangePinyinPreview,
    handleForgotShortcutRef,
    openAnswerModeRef,
    inputTextRef,
    isAnswerModeOpenRef,
    isFocusedRef,
    submitAnswerRef,
  ]);

  const isNativeKeyboardVisible = useCollapsiblePersistentKeyboardDismissButtonVisible();
  const keyWidth = getPinyinKeyboardKeyWidth(containerWidth, false);

  // The grid stays on the card; only the stationary input row is portalled below it.
  const keyboardSlot: ReactNode =
    containerWidth > 0 ? (
      <PinyinKeyboard
        backspaceBehavior={pinyinKeyboardLayout === "qwerty" ? "deleteLastCharacter" : "clearInput"}
        correctPinyinList={correctPinyinList}
        edgeInset
        hideToneKeys
        isVisible={isShuangpinKeyboardVisible}
        layout={pinyinKeyboardLayout === "qwerty" ? "qwerty" : "shuangpin"}
        onCheckFailed={handleCheckFailed}
        onCheckPassed={handleCheckPassed}
        onPinyinSelect={handleChangePinyinPreview}
        pinyinPreview={pinyinPreview}
        resizeOnToggle
        // While expanded, the keyboard's staggered bottom-left slot is the collapse toggle; the
        // collapsed keyboard's toggle is the card's bottom-left icon (`DrillPageContainer`).
        toggleButton={
          isShuangpinKeyboardVisible && !isShuangpinKeyboardToggleHidden ? (
            <FloatingPinyinKeyboardToggleButton
              height={getPinyinKeyboardKeyHeight(keyWidth)}
              isActive
              onPress={onToggleShuangpinKeyboard}
              placement="keyboard-slot"
              width={keyWidth}
            />
          ) : null
        }
      />
    ) : null;

  return (
    <View
      className="relative flex w-full flex-col"
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width);
        setContainerWidth((prev) => (prev === w ? prev : w));
      }}
      pointerEvents="box-none"
    >
      {/* The whole keyboard — keys, the gaps between them and its edge insets — types, and never
          drags (so never reveals) the card. */}
      <LearnFlashcardHorizontalGestureBlocker>
        {keyboardSlot}
      </LearnFlashcardHorizontalGestureBlocker>
      {isCurrent ? (
        <Portal
          className="size-full"
          hostName={`learn-card-answer-input-${sessionId}`}
          name={typeof presentationKey === "string" ? presentationKey : "pronunciation-input"}
        >
          <View className="size-full flex-row items-center gap-2.25 px-1" pointerEvents="box-none">
            {isNativeKeyboardVisible ? <View style={{ width: diameter }} /> : null}
            <View className="h-full min-w-0 flex-1">
              <AnswerInputRow
                accessibilityValue={pinyinPreviewDisplay}
                fillHeight
                isDismissButtonVisible={false}
                onReveal={() => submitAnswerMutation.mutate()}
                revealDisabled={submitAnswerMutation.isPending}
                trailingActions={
                  <View className="shrink-0 flex-row items-center gap-1 pr-1">
                    {TONE_SUBMIT_BUTTONS.map(({ digit, label, mark }) => (
                      <Pressable
                        accessibilityLabel={`${label}: submit answer`}
                        accessibilityRole="button"
                        className="size-7 items-center justify-center rounded-full bg-foreground/10 active:bg-accent disabled:opacity-40"
                        disabled={pinyinPreview.trim().length === 0 || revealMutation.isPending}
                        key={digit}
                        onPress={() => {
                          handleToneSubmit(digit);
                        }}
                        onPressIn={persistentKeyboardKeepFocused}
                        testID={`pronunciation-submit-tone-${digit}`}
                      >
                        <Text className="text-center text-xl leading-7 text-foreground">
                          {mark}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                }
              >
                <DrillFooterPinyinInputbox />
              </AnswerInputRow>
            </View>
          </View>
        </Portal>
      ) : null}
    </View>
  );
}
